import { useEffect, useMemo } from 'react';
import type { MappingProjectReadinessDiff } from '../lib/mappingProjectReadiness';
import { warnMappingFlow } from '../lib/mappingTrace';

/**
 * Surfaces type-mismatch demotions (parameters that exist in the catalog but
 * with an incompatible declared type, so the profile still repairs them) once
 * per change. Kept out of the readiness hook body: this is pure diagnostics,
 * not part of the diff computation.
 */
export function useMappingReadinessTypeMismatchTrace(input: {
  projectKey: string | null;
  modelVersion: number | null;
  catalogDiff: MappingProjectReadinessDiff;
  baseDiff: MappingProjectReadinessDiff;
}): void {
  const { projectKey, modelVersion, catalogDiff, baseDiff } = input;
  const demotedMismatchKey = useMemo(() => {
    const catalogMissing = new Set(
      catalogDiff.missing.map((reference) => reference.name.trim().toLowerCase())
    );
    return baseDiff.missing
      .filter((reference) => !catalogMissing.has(reference.name.trim().toLowerCase()))
      .map((reference) => reference.name)
      .join('|');
  }, [baseDiff, catalogDiff]);

  useEffect(() => {
    if (!demotedMismatchKey) {
      return;
    }

    warnMappingFlow('mapping readiness type mismatch detected', {
      projectKey,
      modelVersion,
      mismatchedParameters: demotedMismatchKey.split('|'),
    });
  }, [demotedMismatchKey, modelVersion, projectKey]);
}
